import './core/helpers/date/date.extensions';

import { Component, effect, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';

import { EMPTY, Subject } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { environment, setEnvironment } from '../environments/environment';
import { applyDefaultFavicons, applyRebrandFavicons } from './core/helpers/apply-rebrand-favicons';
import { applyRebrandBodyClass } from './core/helpers/apply-rebrand-body-class';
import { preloadLibraries } from './core/helpers/preload-libraries';
import { routeToUrl } from './core/helpers/route-to-url';
import { FeatureFlagService } from './core/services/feature-flag.service';
import { RebrandService } from './core/services/rebrand.service';
import { SUPPORTED_BROWSERS_URL } from './project/modules/supported-browsers/supported-browsers.constants';
import { SupportedBrowsersService } from './project/modules/supported-browsers/supported-browsers.service';
import { UpdateService } from './project/modules/sync/update.service';
import { UserService } from './project/modules/user/user.service';
import { WhiteLabelService } from './project/modules/white-label/white-label.service';
import { GoogleAnalyticsService } from './project/services/analytics/google-analytics.service';
import { ClearService } from './project/services/clear.service';
import { TranslationService } from './project/services/translation.service';
import { EAuthRoute } from './project/shared/constants/auth.constants';
import { logErrorInSentry } from './project/modules/errors/response-error';
import { clearUserAuth, userAuthHasExpired } from './core/http/user-auth';
import { EFeatureFlag } from './core/services/feature-flags-consts';

setEnvironment(window.location.host); // Sets if before any app code runs

const APP_ROOT_URL = '/';

@Component({
  selector: 'pp-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss', './project/modules/white-label/white-label.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private userService: UserService,
    private whiteLabelService: WhiteLabelService,
    private updateService: UpdateService,
    private supportedBrowsersService: SupportedBrowsersService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private clearService: ClearService,
    private translationService: TranslationService,
    private featureFlagService: FeatureFlagService,
    private rebrandService: RebrandService,
  ) {
    this.updateService.subscribeToAppUpdates();
    this.featureFlagService.initialize();
    this.applyBrandingOnRebrandChange();

    this.applyRebrandBodyClassOnFlagsChange();
    this.checkUserAuthorisation();

    this.clearService.clearUserCookie();

    if (environment.production) {
      this.googleAnalyticsService.subscribeToRouteChange();
    }

    this.translationService.initTranslations();
    this.whiteLabelService.setDefaultStyle();
  }

  ngOnInit(): void {
    if (!this.checkIfNavigateToUnsupportedBrowser()) {
      this.fetchUser();
    }

    preloadLibraries();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  checkUserAuthorisation(): void {
    if (userAuthHasExpired()) {
      clearUserAuth();
    }
  }

  private applyBrandingOnRebrandChange(): void {
    effect(() => {
      const rebrandEnabled = this.rebrandService.rebrandEnabled();

      if (rebrandEnabled) {
        applyRebrandFavicons();
      } else {
        applyDefaultFavicons();
      }
    });
  }

  private applyRebrandBodyClassOnFlagsChange(): void {
    this.featureFlagService.flagsChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      applyRebrandBodyClass(this.featureFlagService.getFlag(EFeatureFlag.REBRAND));
    });
  }

  private checkIfNavigateToUnsupportedBrowser(): boolean {
    const unsupportedBrowser = this.supportedBrowsersService.isBrowserUnsupported();
    const browserWarningDisabled = this.supportedBrowsersService.readCookie() === 'true';

    if (unsupportedBrowser && !browserWarningDisabled) {
      this.router.navigate([SUPPORTED_BROWSERS_URL]);

      return true;
    }

    return false;
  }

  private fetchUser(): void {
    this.userService
      .fetchUser()
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.tryRedirectToActiveWorkspace();
        }),
        catchError((error) => {
          const isOnLoginPage = window.location.href.includes(EAuthRoute.LOGIN);
          if (!isOnLoginPage) {
            logErrorInSentry({
              error,
              message: `Error fetching user on page ${window.location.href}`,
            });
          }

          this.tryRedirectToLogin();

          return EMPTY;
        }),
      )
      .subscribe();
  }

  private tryRedirectToLogin(): void {
    if (this.isOnAppRoot()) {
      this.router.navigate([routeToUrl(EAuthRoute.LOGIN)]);
    }
  }

  private tryRedirectToActiveWorkspace(): void {
    const user = this.userService.getUser();

    if (!user.activeWorkspaceId) {
      return;
    }

    if (!this.isOnAppRoot()) {
      return;
    }

    this.router.navigate(['/site', user.activeWorkspaceId]);
  }

  private isOnAppRoot(): boolean {
    const navigationInFlight = this.router.currentNavigation()?.extractedUrl.toString();

    return (navigationInFlight ?? this.router.url) === APP_ROOT_URL;
  }
}
